import { AccountBalance, Edit } from '@mui/icons-material';
import { Alert, Box, Card, CardContent, CircularProgress, Typography } from '@mui/material';
import BackendService from 'BackendService';
import React, { useCallback, useEffect, useState } from 'react';
import { InvestorAllocationsResponse } from '../../models/InvestmentEntity';
import Investor from '../../models/Investor';
import ExtendedLiquid from '../../services/ExtendedLiquid';
import { StyledButton } from '../styles/StyledButtons';
import { StyledCard, StyledCardContent, StyledHeading } from '../styles/styles';
import { RedistributeAllocationForm } from './RedistributeAllocationForm';

interface InvestorEntityAllocationsProps {
  investor: Investor;
  fundOpportunityId: string;
  onInvestorUpdate?: (updatedInvestor: Investor) => void;
}

export const InvestorEntityAllocations: React.FC<InvestorEntityAllocationsProps> = ({
  investor,
  onInvestorUpdate,
}) => {
  const [allocations, setAllocations] = useState<InvestorAllocationsResponse | undefined>(
    undefined,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [redistributeMode, setRedistributeMode] = useState(false);

  // Use API response data when available, fallback to investor object
  const committedAmount = allocations?.committedInvestment
    ? Number(allocations.committedInvestment)
    : investor.committedInvestment || 0;
  const totalAllocated = allocations?.totalAllocated || 0;
  const remainingAmount = allocations?.remainingAllocation || 0;

  const loadAllocations = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const backendService = new BackendService();
      const allocationData = await backendService.getInvestorEntityAllocations(investor.id);
      setAllocations(allocationData);
    } catch (err) {
      console.error('Failed to load entity allocations:', err);
      setError('Failed to load entity allocations');
      setAllocations(undefined);
    } finally {
      setLoading(false);
    }
  }, [investor.id]);

  useEffect(() => {
    void loadAllocations();
  }, [loadAllocations]);

  const handleRedistribute = () => {
    setRedistributeMode(true);
  };

  const handleCancelRedistribute = () => {
    setRedistributeMode(false);
  };

  const handleRedistributionSuccess = (updatedInvestor: Investor) => {
    if (onInvestorUpdate) {
      onInvestorUpdate(updatedInvestor);
    }
    // Refresh allocations and exit redistribute mode
    loadAllocations();
    setRedistributeMode(false);
  };

  const handleCreateNewAllocation = () => {
    // Navigate to entity association wizard
    const baseUrl = `/associate-entity/${investor.id}`;
    window.location.href = baseUrl;
  };

  if (loading) {
    return (
      <StyledCard tone="tone1">
        <StyledCardContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
            <Typography sx={{ ml: 2 }}>Loading entity allocations...</Typography>
          </Box>
        </StyledCardContent>
      </StyledCard>
    );
  }

  return (
    <StyledCard tone="tone1">
      <StyledCardContent>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <StyledHeading variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
            <AccountBalance sx={{ mr: 1 }} />
            Investment Entity Allocations
          </StyledHeading>

          {!redistributeMode && !!allocations?.allocations?.length && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <StyledButton
                variant="outlined"
                size="small"
                startIcon={<Edit />}
                onClick={handleRedistribute}
                disabled={allocations.allocations.length === 0}
              >
                Redistribute
              </StyledButton>

              {remainingAmount > 0 && (
                <StyledButton variant="contained" size="small" onClick={handleCreateNewAllocation}>
                  Add Allocation
                </StyledButton>
              )}
            </Box>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Show form when in redistribute mode, otherwise show normal content */}
        {redistributeMode && allocations?.allocations ? (
          <RedistributeAllocationForm
            investor={investor}
            allocations={allocations.allocations.map((allocation) => ({
              id: `${allocation.investorId}-${allocation.investmentEntityId}`,
              investorId: allocation.investorId,
              investmentEntity: {
                id: allocation.investmentEntityId,
                name: allocation.investmentEntityName,
                type: 'COMPANY' as const,
                isOwner: false,
                userRole: 'OWNER' as const,
                createdAt: allocation.createdAt,
                updatedAt: allocation.updatedAt,
              },
              allocatedAmount: Number(allocation.allocatedAmount),
              createdAt: allocation.createdAt,
              updatedAt: allocation.updatedAt,
            }))}
            onSuccess={handleRedistributionSuccess}
            onCancel={handleCancelRedistribute}
          />
        ) : (
          <>
            {/* Summary Section */}
            <Box sx={{ mb: 3, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Total Committed:</strong>{' '}
                {ExtendedLiquid.currencyMillions(committedAmount, false)}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Total Allocated:</strong>{' '}
                {ExtendedLiquid.currencyMillions(totalAllocated, false)}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 'bold',
                  color: remainingAmount > 0 ? 'warning.main' : 'success.main',
                }}
              >
                <strong>Remaining:</strong>{' '}
                {ExtendedLiquid.currencyMillions(remainingAmount, false)}
              </Typography>
            </Box>

            {/* Allocations List */}
            {!allocations?.allocations?.length ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                  No entity allocations yet
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Committed investment needs to be allocated to investment entities
                </Typography>
                <StyledButton variant="contained" onClick={handleCreateNewAllocation}>
                  Create First Allocation
                </StyledButton>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {allocations?.allocations?.map((allocation) => (
                  <Card
                    key={`${allocation.investorId}-${allocation.investmentEntityId}`}
                    variant="outlined"
                    sx={{ position: 'relative' }}
                  >
                    <CardContent>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                        }}
                      >
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="h6" sx={{ mb: 1 }}>
                            {allocation.investmentEntityName}
                          </Typography>

                          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <Typography variant="body2">
                              <strong>Allocated:</strong>{' '}
                              {ExtendedLiquid.currencyMillions(
                                Number(allocation.allocatedAmount),
                                false,
                              )}
                            </Typography>

                            <Typography variant="body2" color="text.secondary">
                              <strong>Percentage:</strong>{' '}
                              {(
                                (Number(allocation.allocatedAmount) / committedAmount) *
                                100
                              ).toFixed(1)}
                              %
                            </Typography>

                            <Typography variant="body2" color="text.secondary">
                              <strong>Created:</strong>{' '}
                              {new Date(allocation.createdAt).toLocaleDateString()}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            )}

            {remainingAmount > 0 && !!allocations?.allocations?.length && (
              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  backgroundColor: 'warning.light',
                  borderRadius: 1,
                }}
              >
                <Alert severity="warning" sx={{ backgroundColor: 'transparent' }}>
                  <Typography variant="body2">
                    <strong>Incomplete Allocation:</strong>{' '}
                    {ExtendedLiquid.currencyMillions(remainingAmount, false)} remaining unallocated.
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    <StyledButton
                      variant="outlined"
                      size="small"
                      onClick={handleCreateNewAllocation}
                    >
                      Allocate Remaining Amount
                    </StyledButton>
                  </Box>
                </Alert>
              </Box>
            )}
          </>
        )}
      </StyledCardContent>
    </StyledCard>
  );
};
